import { useCallback, useMemo, useState } from "react";
import {
  DEFAULT_STUDENT_ACCESSIBILITY_PREFERENCES,
  STUDENT_ACCESSIBILITY_FONT_FAMILIES,
  STUDENT_ACCESSIBILITY_FONT_SIZES,
  STUDENT_ACCESSIBILITY_TEXT_SPACING,
  type StudentAccessibilityPreferences,
} from "./studentAccessibilityApi.js";

const STORAGE_PREFIX = "wmf.staff-accessibility.";
const HEX = /^#[0-9a-fA-F]{6}$/;

export type StaffAccessibilityPreferences = StudentAccessibilityPreferences;

function defaults(): StaffAccessibilityPreferences {
  return { ...DEFAULT_STUDENT_ACCESSIBILITY_PREFERENCES };
}

function browserStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function colour(value: unknown): value is string {
  return value === "default" || (typeof value === "string" && HEX.test(value));
}

/**
 * Treat localStorage as untrusted input. An old build, another script or a
 * curious user can put arbitrary strings there, so only the same choices the
 * accessibility editor can produce are allowed back into the shell.
 */
export function validStaffAccessibilityPreferences(
  value: unknown,
): value is StaffAccessibilityPreferences {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<StaffAccessibilityPreferences>;
  return (
    STUDENT_ACCESSIBILITY_FONT_SIZES.some((option) => option === candidate.fontSize) &&
    STUDENT_ACCESSIBILITY_FONT_FAMILIES.some((option) => option === candidate.fontFamily) &&
    STUDENT_ACCESSIBILITY_TEXT_SPACING.some((option) => option === candidate.textSpacing) &&
    colour(candidate.backgroundColour) &&
    colour(candidate.textColour)
  );
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

/** Staff presentation is a browser preference, scoped to the signed-in account. */
export function readStaffAccessibilityPreferences(
  userId: string,
  storage: Storage | null = browserStorage(),
): StaffAccessibilityPreferences {
  try {
    const raw = storage?.getItem(storageKey(userId));
    if (!raw) return defaults();
    const parsed: unknown = JSON.parse(raw);
    return validStaffAccessibilityPreferences(parsed) ? { ...parsed } : defaults();
  } catch {
    return defaults();
  }
}

export function writeStaffAccessibilityPreferences(
  userId: string,
  preferences: StaffAccessibilityPreferences,
  storage: Storage | null = browserStorage(),
): void {
  if (!validStaffAccessibilityPreferences(preferences)) return;
  try {
    storage?.setItem(storageKey(userId), JSON.stringify(preferences));
  } catch {
    // Cosmetic preference only. Apply it for this session even if persistence
    // is unavailable rather than making the staff Hub unusable.
  }
}

export function resetStaffAccessibilityPreferences(
  userId: string,
  storage: Storage | null = browserStorage(),
): StaffAccessibilityPreferences {
  try {
    storage?.removeItem(storageKey(userId));
  } catch {
    // See writeStaffAccessibilityPreferences: storage failure is non-fatal.
  }
  return defaults();
}

interface StaffAccessibilityState {
  userId: string | null;
  preferences: StaffAccessibilityPreferences;
}

/**
 * Which preferences the staff shell should paint right now.
 *
 * When the signed-in account has changed but our state still describes the
 * previous one, use the value freshly read for the new account rather than the
 * stale state. On a shared browser the old account's cosmetic choices must not
 * be painted into the new account's first staff-shell render, even briefly, and
 * waiting for an effect to resync would do exactly that.
 */
export function resolveStaffAccessibilityPreferences(
  state: StaffAccessibilityState,
  userId: string | null,
  storedForAccount: StaffAccessibilityPreferences,
): StaffAccessibilityPreferences {
  return state.userId === userId ? state.preferences : storedForAccount;
}

export function useStaffAccessibilityPreferences(userId: string | null) {
  const storedForAccount = useMemo(
    () => (userId ? readStaffAccessibilityPreferences(userId) : defaults()),
    [userId],
  );
  const [state, setState] = useState<StaffAccessibilityState>(() => ({
    userId,
    preferences: storedForAccount,
  }));

  const preferences = resolveStaffAccessibilityPreferences(state, userId, storedForAccount);

  // The editor echoes whatever save/reset return back through `apply`, so that
  // is the single place the shell state is updated. save/reset only persist the
  // choice and hand it back.
  const apply = useCallback(
    (next: StaffAccessibilityPreferences) => {
      setState({ userId, preferences: { ...next } });
    },
    [userId],
  );

  const save = useCallback(
    async (next: StaffAccessibilityPreferences): Promise<StaffAccessibilityPreferences> => {
      if (userId) writeStaffAccessibilityPreferences(userId, next);
      return { ...next };
    },
    [userId],
  );

  const reset = useCallback(
    async (): Promise<StaffAccessibilityPreferences> =>
      userId ? resetStaffAccessibilityPreferences(userId) : defaults(),
    [userId],
  );

  return { preferences, save, reset, apply };
}
